/**
 * Client-side redemption: POST a code to the alchemie-commerce
 * /redeemCode endpoint, persist the returned license JWT, and update
 * in-memory auth state.
 *
 * Distinct from `establishLicenseSession`, which expects a JWT (the
 * already-minted license key) and only validates it. Redemption codes
 * are not JWTs — they are short, human-readable bearer tokens like
 * `PIPHAUTHOR_W15V_EJ4T_1CFF` that must be consumed via /redeemCode
 * to produce a JWT.
 *
 * Today this function persists the returned JWT (same shape as
 * establishLicenseSession). PIPH-291 will move that persistence to the
 * redemption code itself — at which point this function's body
 * changes; the call-site signature stays the same.
 */

import { getBearerToken } from '@alchemiesolns/user-access';
import { getLicenseApiBase } from './api';
import { saveCode, saveKey } from './storage';
import { updateAuthState } from './authManager';

/**
 * Server-side RedeemError.kind values, mapped to user-facing strings.
 * Mirrors the enum in alchemie-commerce/functions/src/redemption/redeem.ts
 * plus the endpoint-level `invalid_format` pre-flight rejection.
 */
const REDEEM_ERROR_MESSAGES: Record<string, string> = {
  code_not_found: "This code wasn't found. Check it and try again.",
  already_redeemed: 'This code has already been used.',
  revoked: 'This code is no longer valid.',
  expired: 'This code has expired.',
  orphan_code: "Couldn't apply this code. Please contact support.",
  invalid_format: "That doesn't look like a valid code.",
};

interface RedeemResponseSuccess {
  ok: true;
  licenseId: string;
  key: string;
}

interface RedeemResponseError {
  ok: false;
  error?: { kind?: string };
}

/**
 * Consume a redemption code on the server and establish a session from
 * the returned license JWT. Throws on failure with a user-facing message
 * suitable for display in the redeem UI.
 */
export async function redeemCode(code: string): Promise<void> {
  const trimmed = code.trim();
  if (!trimmed) {
    throw new Error('Redemption code is required');
  }

  const idToken = await getBearerToken();
  if (!idToken) {
    throw new Error('Sign in required to redeem a code');
  }

  console.log('[Auth API] Redeeming code');

  let response: Response;
  try {
    response = await fetch(`${getLicenseApiBase()}/redeemCode`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ code: trimmed }),
    });
  } catch (err) {
    console.error('[Auth API] Network error during redeem:', err);
    throw new Error('Network error — please try again.');
  }

  let data: RedeemResponseSuccess | RedeemResponseError | undefined;
  try {
    data = (await response.json()) as RedeemResponseSuccess | RedeemResponseError;
  } catch {
    data = undefined;
  }

  if (!response.ok || !data?.ok) {
    const kind = (data as RedeemResponseError | undefined)?.error?.kind;
    const message =
      (kind && REDEEM_ERROR_MESSAGES[kind]) || 'Could not redeem this code.';
    console.error(
      `[Auth API] Redeem failed (status=${response.status}, kind=${kind ?? 'unknown'})`,
    );
    throw new Error(message);
  }

  const success = data as RedeemResponseSuccess;
  // Persist the code as the long-lived identity (PIPH-291) — used to
  // re-mint a fresh JWT on each app boot. The JWT itself is still
  // persisted alongside for now; eventual tightening may move it to
  // in-memory only.
  saveCode(trimmed);
  saveKey(success.key);
  updateAuthState(true, success.key);
  console.log('[Auth API] Redeem succeeded, license established');
}
